import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import cn from 'classnames';

import { range } from '../../helpers';
import { useCampaignContent } from '../../hooks/useCampaignContent';
import { FEED_LIMIT } from '../../model/HomepageFeed';
import { formatPrice } from '../../model/Price';
import { getVehiclePrice, getVehiclePriceListItem, getVehiclePricePerDay } from '../../model/VehicleDetailPrices';
import RouteEnum from '../../RouteEnum';
import Tracking from '../../services/Tracking';
import type { ReduxState } from '../../store';
import Image from '../Image';
import Label from '../Label';
import Link from '../Link';
import NumberFormat from '../NumberFormat';
import Price from '../Price';
import { RestrictionProvider } from '../Restriction';
import T from '../Translate';
import { CarCardSkeleton } from '../Vehicle/CarCard';
import CarDetailLink from '../Vehicle/CarDetailLink';

import styles from './vehicleFeed.module.scss';

interface Props {
  className?: string;
}

type NoOfDays = 1 | 3 | 7 | 14 | 30;

const buttons: { noOfDays: NoOfDays; text: string; label?: string }[] = [
  {
    noOfDays: 3,
    text: 'homepage.feed.range.per3Days',
    label: 'homepage.feed.range.per3Days.label',
  },
  {
    noOfDays: 7,
    text: 'homepage.feed.range.perWeek',
    label: 'homepage.feed.range.perWeek.label',
  },
  {
    noOfDays: 14,
    text: 'homepage.feed.range.per2Weeks',
    label: 'homepage.feed.range.per2Weeks.label',
  },
  {
    noOfDays: 30,
    text: 'homepage.feed.range.perMonth',
    label: 'homepage.feed.range.perMonth.label',
  },
];

const totalPriceContextMapping: Record<NoOfDays, string> = {
  1: '',
  3: '3days',
  7: 'week',
  14: '2weeks',
  30: 'month',
};

export default function VehicleFeed({ className }: Props) {
  const campaign = useCampaignContent();
  const { data, loading } = useSelector((store: ReduxState) => store.app.feed);
  const [noOfDays, setNoOfDays] = useState<NoOfDays>(3);
  const isMoreDaysSelected = useMemo(() => noOfDays > 1, [noOfDays]);

  const handleRangeClick = (nextRange: NoOfDays) => () => {
    Tracking.track('HP_FEED_PRICE_RANGE_CLICKED', nextRange);
    setNoOfDays(nextRange);
  };

  return (
    <div className={cn(styles.container, className)}>
      <T as="h2" className={styles.title} html id="homepage.feed.title" />
      <T as="p" className={styles.description} id="homepage.feed.description" />

      <div className={styles.range}>
        {buttons.map(({ noOfDays: range, text, label }) => (
          <button
            className={cn(styles.tab, { active: noOfDays === range })}
            key={range}
            onClick={handleRangeClick(range)}
            type="button"
          >
            <T as="span" id={text} />
            {label ? (
              <Label className={styles.label}>
                <T id={label} />
              </Label>
            ) : null}
          </button>
        ))}
      </div>

      <div className={styles.vehicles}>
        {loading
          ? range(0, FEED_LIMIT).map(it => <CarCardSkeleton className={styles.feedItem} key={it} />)
          : data.map(it => {
              const pricePerDay = getVehiclePriceListItem(it.prices, noOfDays);
              const pricePerOneDay = getVehiclePricePerDay(it.prices, 1);
              const totalPrice = getVehiclePrice(noOfDays, it.prices);

              return (
                <CarDetailLink
                  bodyType={it.type}
                  className={styles.vehicle}
                  fuel={it.fuel}
                  hash={it.hash}
                  key={it.hash}
                  manufacturer={it.manufacturer}
                  model={it.model}
                  onClick={() => Tracking.track('HP_FEED_CAR_CLICKED')}
                >
                  <RestrictionProvider region={it.country.alpha2_code}>
                    <div className={styles.vehicleImage}>
                      <Image alt="car" loading="lazy" size="md" src={it.main_image} />
                    </div>

                    {isMoreDaysSelected ? (
                      <Label className={styles.vehicleDiscount}>
                        <NumberFormat fractions={[0, 1]} negative suffix="%" value={pricePerDay.flexible_discount} />
                      </Label>
                    ) : null}

                    <div className={styles.vehicleContent}>
                      <h4 className="small">{it.name}</h4>

                      <div className={styles.vehicleRow}>
                        <div
                          className={cn(styles.vehicleRating, {
                            'no-review': !it.ratings_count,
                          })}
                        >
                          <i className="icon icon-star" />
                          {it.ratings_count ? (
                            <NumberFormat fractions={2} value={it.rating_average_float} />
                          ) : (
                            <T id="homepage.feed.card.noReviews" />
                          )}
                        </div>

                        <div className={styles.vehiclePrices}>
                          <T as="span" id="homepage.feed.card.pricePerDay" />
                          <div className={styles.vehiclePricePerDay}>
                            {isMoreDaysSelected && <Price className={styles.pricePerOneDay} price={pricePerOneDay} />}
                            <Price className={styles.pricePerDay} price={pricePerDay.price} />
                          </div>
                          {isMoreDaysSelected && (
                            <T
                              className={styles.totalPrice}
                              context={totalPriceContextMapping[noOfDays]}
                              data={{ price: formatPrice(totalPrice) }}
                              id="homepage.feed.card.totalPrice"
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </RestrictionProvider>
                </CarDetailLink>
              );
            })}
      </div>

      <div className={styles.buttons}>
        {campaign ? (
          <Link
            className="button button-mono button-lg button-icon"
            search={campaign.filtersQuery}
            to={RouteEnum.RENT_CAR}
          >
            {campaign.button}
            <i className="icon icon-search" />
          </Link>
        ) : null}
        <Link
          className={cn('button button-mono button-lg button-icon', {
            'button-outline': !!campaign,
          })}
          onClick={() => Tracking.track('HP_FEED_SEARCH_ALL_CLICKED')}
          to={RouteEnum.RENT_CAR}
        >
          <T id="homepage.feed.link.browseCars" />
          {!campaign ? <i className="icon icon-arrow-right-circle" /> : null}
        </Link>
      </div>
    </div>
  );
}
